/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',
  MARK_RECEIVED_FROM_PURCHASED_OPERATOR: 'transition/mark-received-from-purchased-operator',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',
  OPERATOR_MARK_RECEIVED: 'transition/operator-mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  EVIDENCE_EXCHANGE_BY_SELLER: 'transition/evidence-exchange-by-seller',
  EVIDENCE_EXCHANGE_BY_SELLER_AGAIN: 'transition/evidence-exchange-by-seller-again',
  EVIDENCE_EXCHANGE_BY_BUYER: 'transition/evidence-exchange-by-buyer',
  EVIDENCE_EXCHANGE_BY_BUYER_AGAIN: 'transition/evidence-exchange-by-buyer-again',
  ESCALATE_BY_SELLER: 'transition/escalate-by-seller',
  ESCALATE_BY_BUYER: 'transition/escalate-by-buyer',
  REFUND_ISSUED_RETURN_REQUIRED: 'transition/refund-issued-return-required',
  RETURN_RECEVIED: 'transition/return-received',
  PROBLEM_WITH_RETURNED_ITEM: 'transition/problem-with-returned-item',
  OPERATOR_DISPUTE: 'transition/operator-dispute',
  FINAL_REVIEW_AND_REFUND: 'transition/final-review-and-refund',
  FINAL_REVIEW_AND_FUND_RELEASED: 'transition/final-review-and-fund-released',
  REFUND_WITHOUT_RETURN: 'transition/refund-without-return',
  FUNDS_RELEASED: 'transition/funds-released',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  // Offer transitions
  SEND_OFFER: 'transition/send-offer',
  EXPIRE_OFFER: 'transition/expire-offer',
  ACCEPT_OFFER: 'transition/accept-offer',
  DECLINE_OFFER: 'transition/decline-offer',
  COUNTER_OFFER: 'transition/counter-offer',
  ACCEPT_AFTER_COUNTER_1: 'transition/accept-after-counter-1',
  DECLINE_AFTER_COUNTER_1: 'transition/decline-after-counter-1',
  COUNTER_AFTER_COUNTER_1: 'transition/counter-after-counter-1',
  ACCEPT_AFTER_COUNTER_2: 'transition/accept-after-counter-2',
  DECLINE_AFTER_COUNTER_2: 'transition/decline-after-counter-2',
  COUNTER_AFTER_COUNTER_2: 'transition/counter-after-counter-2',
  ACCEPT_AFTER_COUNTER_3: 'transition/accept-after-counter-3',
  DECLINE_AFTER_COUNTER_3: 'transition/decline-after-counter-3',
  REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED: 'transition/request-payment-after-offer-accepted',

  // Paypal transitions
  REQUEST_PAYMENT_WITH_PAYPAL: 'transition/request-payment-with-paypal',
  CANCEL_WITH_PAYPAL: 'transition/cancel-with-paypal',
  REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL: 'transition/request-payment-after-enquiry-with-paypal',
  REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_PAYPAL: 'transition/request-payment-after-offer-accepted-with-paypal',
  CONFIRM_PAYMENT_WITH_PAYPAL: 'transition/confirm-payment-with-paypal',
  EXPIRE_PAYMENT_WITH_PAYPAL: 'transition/expire-payment-with-paypal',
  MARK_DELIVERED_WITH_PAYPAL: 'transition/mark-delivered-with-paypal',
  MARK_DELIVERED_WITH_PAYPAL_OPERATOR: 'transition/mark-delivered-with-paypal-operator',
  MARK_RECEIVED_FROM_PURCHASED_WITH_PAYPAL: 'transition/mark-received-from-purchased-with-paypal',
  MARK_RECEIVED_FROM_PURCHASED_WITH_PAYPAL_OPERATOR:
    'transition/mark-received-from-purchased-with-paypal-operator',
  AUTO_CANCEL_WITH_PAYPAL: 'transition/auto-cancel-with-paypal',
  MARK_RECEIVED_WITH_PAYPAL: 'transition/mark-received-with-paypal',
  MARK_RECEIVED_WITH_PAYPAL_OPERATOR: 'transition/mark-received-with-paypal-operator',
  AUTO_MARK_RECEIVED_WITH_PAYPAL: 'transition/auto-mark-received-with-paypal',
  DISPUTE_WITH_PAYPAL: 'transition/dispute-with-paypal',
  OPERATOR_DISPUTE_WITH_PAYPAL: 'transition/operator-dispute-with-paypal',
  MARK_RECEIVED_FROM_DISPUTED_WITH_PAYPAL: 'transition/mark-received-from-disputed-with-paypal',

  AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL: 'transition/auto-cancel-from-disputed-with-paypal',
  CANCEL_FROM_DISPUTED_WITH_PAYPAL: 'transition/cancel-from-disputed-with-paypal',

  EVIDENCE_EXCHANGE_BY_SELLER_WITH_PAYPAL: 'transition/evidence-exchange-by-seller-with-paypal',
  EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_PAYPAL: 'transition/evidence-exchange-by-seller-again-with-paypal',
  EVIDENCE_EXCHANGE_BY_BUYER_WITH_PAYPAL: 'transition/evidence-exchange-by-buyer-with-paypal',
  EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_PAYPAL: 'transition/evidence-exchange-by-buyer-again-with-paypal',
  ESCALATE_BY_SELLER_WITH_PAYPAL: 'transition/escalate-by-seller-with-paypal',
  ESCALATE_BY_BUYER_WITH_PAYPAL: 'transition/escalate-by-buyer-with-paypal',
  REFUND_ISSUED_RETURN_REQUIRED_WITH_PAYPAL: 'transition/refund-issued-return-required-with-paypal',
  RETURN_RECEVIED_WITH_PAYPAL: 'transition/return-received-with-paypal',
  PROBLEM_WITH_RETURNED_ITEM_WITH_PAYPAL: 'transition/problem-with-returned-item-with-paypal',
  FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_PAYPAL: 'transition/final-review-and-refund-with-paypal',
  FINAL_REVIEW_AND_FUND_RELEASED_WITH_PAYPAL: 'transition/final-review-and-fund-released-with-paypal',
  REFUND_WITHOUT_RETURN_WITH_PAYPAL: 'transition/refund-without-return-with-paypal',
  FUNDS_RELEASED_WITH_PAYPAL: 'transition/funds-released-with-paypal',



  // Wallet transitions
  REQUEST_PAYMENT_WITH_WALLET: 'transition/request-payment-with-wallet',
  CANCEL_WITH_WALLET: 'transition/cancel-with-wallet',
  REQUEST_PAYMENT_AFTER_INQUIRY_WITH_WALLET: 'transition/request-payment-after-enquiry-with-wallet',
  REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_WALLET: 'transition/request-payment-after-offer-accepted-with-wallet',
  CONFIRM_PAYMENT_WITH_WALLET: 'transition/confirm-payment-with-wallet',
  EXPIRE_PAYMENT_WITH_WALLET: 'transition/expire-payment-with-wallet',
  MARK_DELIVERED_WITH_WALLET: 'transition/mark-delivered-with-wallet',
  MARK_DELIVERED_WITH_WALLET_OPERATOR: 'transition/mark-delivered-with-wallet-operator',
  MARK_RECEIVED_FROM_PURCHASED_WITH_WALLET: 'transition/mark-received-from-purchased-with-wallet',
  MARK_RECEIVED_FROM_PURCHASED_WITH_WALLET_OPERATOR:
    'transition/mark-received-from-purchased-with-wallet-operator',
  AUTO_CANCEL_WITH_WALLET: 'transition/auto-cancel-with-wallet',
  MARK_RECEIVED_WITH_WALLET: 'transition/mark-received-with-wallet',
  MARK_RECEIVED_WITH_WALLET_OPERATOR: 'transition/mark-received-with-wallet-operator',
  AUTO_MARK_RECEIVED_WITH_WALLET: 'transition/auto-mark-received-with-wallet',
  DISPUTE_WITH_WALLET: 'transition/dispute-with-wallet',
  OPERATOR_DISPUTE_WITH_WALLET: 'transition/operator-dispute-with-wallet',
  AUTO_CANCEL_FROM_DISPUTED_WITH_WALLET: 'transition/auto-cancel-from-disputed-with-wallet',
  CANCEL_FROM_DISPUTED_WITH_WALLET: 'transition/cancel-from-disputed-with-wallet',
  MARK_RECEIVED_FROM_DISPUTED_WITH_WALLET: 'transition/mark-received-from-disputed-with-wallet',

  EVIDENCE_EXCHANGE_BY_SELLER_WITH_WALLET: 'transition/evidence-exchange-by-seller-with-wallet',
  EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_WALLET: 'transition/evidence-exchange-by-seller-again-with-wallet',
  EVIDENCE_EXCHANGE_BY_BUYER_WITH_WALLET: 'transition/evidence-exchange-by-buyer-with-wallet',
  EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_WALLET: 'transition/evidence-exchange-by-buyer-again-with-wallet',
  ESCALATE_BY_SELLER_WITH_WALLET: 'transition/escalate-by-seller-with-wallet',
  ESCALATE_BY_BUYER_WITH_WALLET: 'transition/escalate-by-buyer-with-wallet',
  REFUND_ISSUED_RETURN_REQUIRED_WITH_WALLET: 'transition/refund-issued-return-required-with-wallet',
  RETURN_RECEVIED_WITH_WALLET: 'transition/return-received-with-wallet',
  PROBLEM_WITH_RETURNED_ITEM_WITH_WALLET: 'transition/problem-with-returned-item-with-wallet',
  FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_WALLET: 'transition/final-review-and-refund-with-wallet',
  FINAL_REVIEW_AND_FUND_RELEASED_WITH_WALLET: 'transition/final-review-and-fund-released-with-wallet',
  REFUND_WITHOUT_RETURN_WITH_WALLET: 'transition/refund-without-return-with-wallet',
  FUNDS_RELEASED_WITH_WALLET: 'transition/funds-released-with-wallet',

};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  RETURN_ISSUE: 'return-issue',

  REFUND_ISSUED: 'refund-issued',

  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  REFUNDED: 'refunded',
  EXCHANGE: 'exchange',
  ESCALATE: 'escalate',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',

  //  Offer states
  OFFER_SENT: 'offer-sent',
  COUNTER_1: 'counter-1',
  COUNTER_2: 'counter-2',
  COUNTER_3: 'counter-3',
  OFFER_DECLINED: 'offer-declined',
  OFFER_ACCEPTED: 'offer-accepted',
  OFFER_EXPIRED: 'offer-expired',

  DISPUTED_WITH_PAYPAL: 'disputed-with-paypal',
  EXCHANGE_WITH_PAYPAL: 'exchange-with-paypal',
  ESCALATE_WITH_PAYPAL: 'escalate-with-paypal',
  REFUND_ISSUED_WITH_PAYPAL: 'refund-issued-with-paypal',
  REFUNDED_WITH_PAYPAL: 'refunded-with-paypal',
  RETURN_ISSUE_WITH_PAYPAL: 'return-issue-with-paypal',

  PENDING_PAYMENT_WITH_PAYPAL: 'pending-paypal-payment',
  PAYMENT_EXPIRED_WITH_PAYPAL: 'payment-with-paypal-expired',
  PURCHASED_WITH_PAYPAL: 'purchased-with-paypal',
  DELIVERED_WITH_PAYPAL: 'delivered-with-paypal',
  DISPUTED_WITH_PAYPAL: 'disputed-with-paypal',
  CANCELED_WITH_PAYPAL: 'canceled-with-paypal',

  PENDING_PAYMENT_WITH_WALLET: 'pending-wallet-payment',
  PAYMENT_EXPIRED_WITH_WALLET: 'payment-with-wallet-expired',
  PURCHASED_WITH_WALLET: 'purchased-with-wallet',
  DELIVERED_WITH_WALLET: 'delivered-with-wallet',
  DISPUTED_WITH_WALLET: 'disputed-with-wallet',
  CANCELED_WITH_WALLET: 'canceled-with-wallet',

  DISPUTED_WITH_WALLET: 'disputed-with-wallet',
  EXCHANGE_WITH_WALLET: 'exchange-with-wallet',
  ESCALATE_WITH_WALLET: 'escalate-with-wallet',
  REFUND_ISSUED_WITH_WALLET: 'refund-issued-with-wallet',
  REFUNDED_WITH_WALLET: 'refunded-with-wallet',
  RETURN_ISSUE_WITH_WALLET: 'return-issue-with-wallet',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.SEND_OFFER]: states.OFFER_SENT,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_WITH_PAYPAL]: states.PENDING_PAYMENT_WITH_PAYPAL,
        [transitions.REQUEST_PAYMENT_WITH_WALLET]: states.PENDING_PAYMENT_WITH_WALLET,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL]: states.PENDING_PAYMENT_WITH_PAYPAL,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_WALLET]: states.PENDING_PAYMENT_WITH_WALLET,
      },
    },

    [states.PENDING_PAYMENT_WITH_PAYPAL]: {
      on: {
        [transitions.EXPIRE_PAYMENT_WITH_PAYPAL]: states.PAYMENT_EXPIRED_WITH_PAYPAL,
        [transitions.CONFIRM_PAYMENT_WITH_PAYPAL]: states.PURCHASED_WITH_PAYPAL,
      },
    },

    [states.PAYMENT_EXPIRED_WITH_PAYPAL]: {},

    [states.PURCHASED_WITH_PAYPAL]: {
      on: {
        [transitions.MARK_DELIVERED_WITH_PAYPAL]: states.DELIVERED_WITH_PAYPAL,
        [transitions.MARK_DELIVERED_WITH_PAYPAL_OPERATOR]: states.DELIVERED_WITH_PAYPAL,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED_WITH_PAYPAL]: states.RECEIVED,
        [transitions.AUTO_CANCEL_WITH_PAYPAL]: states.CANCELED_WITH_PAYPAL,
        [transitions.CANCEL_WITH_PAYPAL]: states.CANCELED_WITH_PAYPAL,
      },
    },
    [states.DELIVERED_WITH_PAYPAL]: {
      on: {
        [transitions.MARK_RECEIVED_WITH_PAYPAL]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED_WITH_PAYPAL]: states.RECEIVED,
        [transitions.DISPUTE_WITH_PAYPAL]: states.DISPUTED_WITH_PAYPAL,
        [transitions.OPERATOR_DISPUTE_WITH_PAYPAL]: states.DISPUTED_WITH_PAYPAL,
      },
    },

      [states.DISPUTED_WITH_PAYPAL]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL]: states.CANCELED,
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER_WITH_PAYPAL]: states.EXCHANGE_WITH_PAYPAL,
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER_WITH_PAYPAL]: states.EXCHANGE_WITH_PAYPAL,
        [transitions.MARK_RECEIVED_FROM_DISPUTED_WITH_PAYPAL]: states.RECEIVED,
      },
    },
    [states.EXCHANGE_WITH_PAYPAL]: {
      on: {
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_PAYPAL]: states.EXCHANGE_WITH_PAYPAL,
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_PAYPAL]: states.EXCHANGE_WITH_PAYPAL,
        [transitions.ESCALATE_BY_SELLER_WITH_PAYPAL]: states.ESCALATE_WITH_PAYPAL,
        [transitions.ESCALATE_BY_BUYER_WITH_PAYPAL]: states.ESCALATE_WITH_PAYPAL,
      },
    },
    [states.ESCALATE_WITH_PAYPAL]: {
      on: {
        [transitions.REFUND_ISSUED_RETURN_REQUIRED_WITH_PAYPAL]: states.REFUND_ISSUED_WITH_PAYPAL,
        [transitions.REFUND_WITHOUT_RETURN_WITH_PAYPAL]: states.REFUNDED_WITH_PAYPAL,
        [transitions.FUNDS_RELEASED_WITH_PAYPAL]: states.RECEIVED,
      },
    },
    [states.REFUND_ISSUED_WITH_PAYPAL]: {
      on: {
        [transitions.RETURN_RECEVIED_WITH_PAYPAL]: states.REFUNDED_WITH_PAYPAL,
        [transitions.PROBLEM_WITH_RETURNED_ITEM_WITH_PAYPAL]: states.RETURN_ISSUE_WITH_PAYPAL,
      },
    },
    [states.RETURN_ISSUE_WITH_PAYPAL]: {
      on: {
        [transitions.FINAL_REVIEW_AND_FUND_RELEASED_WITH_PAYPAL]: states.RECEIVED,
        [transitions.FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_PAYPAL]: states.REFUNDED_WITH_PAYPAL,
      },
    },

    [states.PENDING_PAYMENT_WITH_WALLET]: {
      on: {
        [transitions.EXPIRE_PAYMENT_WITH_WALLET]: states.PAYMENT_EXPIRED_WITH_WALLET,
        [transitions.CONFIRM_PAYMENT_WITH_WALLET]: states.PURCHASED_WITH_WALLET,
      },
    },

    [states.PAYMENT_EXPIRED_WITH_WALLET]: {},

    [states.PURCHASED_WITH_WALLET]: {
      on: {
        [transitions.MARK_DELIVERED_WITH_WALLET]: states.DELIVERED_WITH_WALLET,
        [transitions.MARK_DELIVERED_WITH_WALLET_OPERATOR]: states.DELIVERED_WITH_WALLET,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED_WITH_WALLET]: states.RECEIVED,
        [transitions.AUTO_CANCEL_WITH_WALLET]: states.CANCELED_WITH_WALLET,
        [transitions.CANCEL_WITH_WALLET]: states.CANCELED_WITH_WALLET,
      },
    },
    [states.DELIVERED_WITH_WALLET]: {
      on: {
        [transitions.MARK_RECEIVED_WITH_WALLET]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED_WITH_WALLET]: states.RECEIVED,
        [transitions.DISPUTE_WITH_WALLET]: states.DISPUTED_WITH_WALLET,
        [transitions.OPERATOR_DISPUTE_WITH_WALLET]: states.DISPUTED_WITH_WALLET,
      },
    },

        [states.DISPUTED_WITH_WALLET]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_WALLET]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED_WITH_WALLET]: states.CANCELED,
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER_WITH_WALLET]: states.EXCHANGE_WITH_WALLET,
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER_WITH_WALLET]: states.EXCHANGE_WITH_WALLET,
        [transitions.MARK_RECEIVED_FROM_DISPUTED_WITH_WALLET]: states.RECEIVED,
      },
    },
    [states.EXCHANGE_WITH_WALLET]: {
      on: {
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_WALLET]: states.EXCHANGE_WITH_WALLET,
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_WALLET]: states.EXCHANGE_WITH_WALLET,
        [transitions.ESCALATE_BY_SELLER_WITH_WALLET]: states.ESCALATE_WITH_WALLET,
        [transitions.ESCALATE_BY_BUYER_WITH_WALLET]: states.ESCALATE_WITH_WALLET,
      },
    },
    [states.ESCALATE_WITH_WALLET]: {
      on: {
        [transitions.REFUND_ISSUED_RETURN_REQUIRED_WITH_WALLET]: states.REFUND_ISSUED_WITH_WALLET,
        [transitions.REFUND_WITHOUT_RETURN_WITH_WALLET]: states.REFUNDED_WITH_WALLET,
        [transitions.FUNDS_RELEASED_WITH_WALLET]: states.RECEIVED,
      },
    },
    [states.REFUND_ISSUED_WITH_WALLET]: {
      on: {
        [transitions.RETURN_RECEVIED_WITH_WALLET]: states.REFUNDED_WITH_WALLET,
        [transitions.PROBLEM_WITH_RETURNED_ITEM_WITH_WALLET]: states.RETURN_ISSUE_WITH_WALLET,
      },
    },
    [states.RETURN_ISSUE_WITH_WALLET]: {
      on: {
        [transitions.FINAL_REVIEW_AND_FUND_RELEASED_WITH_WALLET]: states.RECEIVED,
        [transitions.FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_WALLET]: states.REFUNDED_WITH_WALLET,
      },
    },

    [states.OFFER_SENT]: {
      on: {
        [transitions.ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.EXPIRE_OFFER]: states.OFFER_EXPIRED,
        [transitions.COUNTER_OFFER]: states.COUNTER_1,
      },
    },

    [states.COUNTER_1]: {
      on: {
        [transitions.ACCEPT_AFTER_COUNTER_1]: states.OFFER_ACCEPTED,
        [transitions.DECLINE_AFTER_COUNTER_1]: states.OFFER_DECLINED,
        [transitions.COUNTER_AFTER_COUNTER_1]: states.COUNTER_2,
      },
    },

    [states.COUNTER_2]: {
      on: {
        [transitions.ACCEPT_AFTER_COUNTER_2]: states.OFFER_ACCEPTED,
        [transitions.DECLINE_AFTER_COUNTER_2]: states.OFFER_DECLINED,
        [transitions.COUNTER_AFTER_COUNTER_2]: states.COUNTER_3,
      },
    },

    [states.COUNTER_3]: {
      on: {
        [transitions.ACCEPT_AFTER_COUNTER_3]: states.OFFER_ACCEPTED,
        [transitions.DECLINE_AFTER_COUNTER_3]: states.OFFER_DECLINED,
      },
    },

    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_PAYPAL]: states.PENDING_PAYMENT_WITH_PAYPAL,
        [transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_WALLET]: states.PENDING_PAYMENT_WITH_WALLET,
      },
    },
    [states.OFFER_DECLINED]: {},
    [states.OFFER_EXPIRED]: {},
    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},

    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER]: states.EXCHANGE,
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER]: states.EXCHANGE,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },
    [states.EXCHANGE]: {
      on: {
        [transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN]: states.EXCHANGE,
        [transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN]: states.EXCHANGE,
        [transitions.ESCALATE_BY_SELLER]: states.ESCALATE,
        [transitions.ESCALATE_BY_BUYER]: states.ESCALATE,
      },
    },
    [states.ESCALATE]: {
      on: {
        [transitions.REFUND_ISSUED_RETURN_REQUIRED]: states.REFUND_ISSUED,
        [transitions.REFUND_WITHOUT_RETURN]: states.REFUNDED,
        [transitions.FUNDS_RELEASED]: states.RECEIVED,
      },
    },
    [states.REFUND_ISSUED]: {
      on: {
        [transitions.RETURN_RECEVIED]: states.REFUNDED,
        [transitions.PROBLEM_WITH_RETURNED_ITEM]: states.RETURN_ISSUE,
      },
    },
    [states.RETURN_ISSUE]: {
      on: {
        [transitions.FINAL_REVIEW_AND_FUND_RELEASED]: states.RECEIVED,
        [transitions.FINAL_REVIEW_AND_REFUND]: states.REFUNDED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_PAYPAL,
    transitions.CONFIRM_PAYMENT_WITH_WALLET,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_WITH_PAYPAL,
    transitions.AUTO_CANCEL_WITH_WALLET,
    transitions.CANCEL,
    transitions.CANCEL_WITH_PAYPAL,
    transitions.CANCEL_WITH_WALLET,
    transitions.MARK_DELIVERED,
    transitions.MARK_DELIVERED_WITH_PAYPAL,
    transitions.MARK_DELIVERED_WITH_PAYPAL_OPERATOR,
    transitions.MARK_DELIVERED_WITH_WALLET,
    transitions.MARK_DELIVERED_WITH_WALLET_OPERATOR,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.DISPUTE,
    transitions.DISPUTE_WITH_PAYPAL,
    transitions.MARK_RECEIVED_FROM_DISPUTED_WITH_PAYPAL,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER_WITH_PAYPAL,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_PAYPAL,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER_WITH_PAYPAL,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_PAYPAL,
    transitions.ESCALATE_BY_SELLER_WITH_PAYPAL,
    transitions.ESCALATE_BY_BUYER_WITH_PAYPAL,
    transitions.REFUND_ISSUED_RETURN_REQUIRED_WITH_PAYPAL,
    transitions.RETURN_RECEVIED_WITH_PAYPAL,
    transitions.PROBLEM_WITH_RETURNED_ITEM_WITH_PAYPAL,
    transitions.FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_PAYPAL,
    transitions.FINAL_REVIEW_AND_FUND_RELEASED_WITH_PAYPAL,
    transitions.REFUND_WITHOUT_RETURN_WITH_PAYPAL,
    transitions.FUNDS_RELEASED_WITH_PAYPAL,
    transitions.DISPUTE_WITH_WALLET,
    transitions.OPERATOR_DISPUTE_WITH_WALLET,
    transitions.MARK_RECEIVED_FROM_DISPUTED_WITH_WALLET,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER_WITH_WALLET,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN_WITH_WALLET,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER_WITH_WALLET,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN_WITH_WALLET,
    transitions.ESCALATE_BY_SELLER_WITH_WALLET,
    transitions.ESCALATE_BY_BUYER_WITH_WALLET,
    transitions.REFUND_ISSUED_RETURN_REQUIRED_WITH_WALLET,
    transitions.RETURN_RECEVIED_WITH_WALLET,
    transitions.OPERATOR_DISPUTE_WITH_PAYPAL,
    transitions.PROBLEM_WITH_RETURNED_ITEM_WITH_WALLET,
    transitions.FINAL_REVIEW_AND_REFUND_WITH_REFUND_WITH_WALLET,
    transitions.FINAL_REVIEW_AND_FUND_RELEASED_WITH_WALLET,
    transitions.REFUND_WITHOUT_RETURN_WITH_WALLET,
    transitions.FUNDS_RELEASED_WITH_WALLET,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER,
    transitions.EVIDENCE_EXCHANGE_BY_SELLER_AGAIN,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER,
    transitions.EVIDENCE_EXCHANGE_BY_BUYER_AGAIN,
    transitions.ESCALATE_BY_SELLER,
    transitions.ESCALATE_BY_BUYER,
    transitions.REFUND_ISSUED_RETURN_REQUIRED,
    transitions.RETURN_RECEVIED,
    transitions.PROBLEM_WITH_RETURNED_ITEM,
    transitions.FINAL_REVIEW_AND_REFUND,
    transitions.FINAL_REVIEW_AND_FUND_RELEASED,
    transitions.REFUND_WITHOUT_RETURN,
    transitions.FUNDS_RELEASED,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL,
    transitions.AUTO_CANCEL_FROM_DISPUTED_WITH_WALLET,
    transitions.CANCEL_FROM_DISPUTED_WITH_WALLET,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED,
    transitions.REQUEST_PAYMENT_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_WITH_WALLET,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_WALLET,
    transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED_WITH_WALLET
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,

    // Paypal transitions
    transitions.EXPIRE_PAYMENT_WITH_PAYPAL,
    transitions.CANCEL_WITH_PAYPAL,
    transitions.AUTO_CANCEL_WITH_PAYPAL,
    transitions.AUTO_MARK_RECEIVED_WITH_PAYPAL,
    transitions.CANCEL_FROM_DISPUTED_WITH_PAYPAL,

    // Wallet transitions
    transitions.EXPIRE_PAYMENT_WITH_WALLET,
    transitions.CANCEL_WITH_WALLET,
    transitions.AUTO_CANCEL_WITH_WALLET,
    transitions.AUTO_MARK_RECEIVED_WITH_WALLET,
    transitions.CANCEL_FROM_DISPUTED_WITH_WALLET,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PURCHASED];
